import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import logoByline from "../../assets/images/esg2go_logo_byline.svg";
import {
  defaultLanguage,
  normalizeSupportedLanguage,
  SupportedLanguage,
} from "../../language/supportedLanguages";
import { SiteNavigationItem, siteLinks } from "./siteLinks";

const buildLanguageParams = (
  language: SupportedLanguage,
  currentSearchParams: URLSearchParams
) => {
  const params = new URLSearchParams(currentSearchParams.toString());
  params.delete("language");

  if (language === defaultLanguage) {
    params.delete("lang");
  } else {
    params.set("lang", language);
  }

  return params;
};

const mobileNavigationClassName = "site-nav--locked";
const mobileNavigationMediaQuery = "(max-width: 980px)";

const getNavigationItemClassName = (item: SiteNavigationItem) => {
  const classNames = ["site-nav__item"];

  if (item.children?.length) {
    classNames.push("site-nav__item--has-children");
  }

  if (item.isCallToAction) {
    classNames.push("site-nav__item--cta");
  }

  return classNames.join(" ");
};

const Navigation: React.FunctionComponent = () => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [navIsOpen, setNavIsOpen] = useState<boolean>(false);

  const normalizeLanguage = (lang: string | null) => {
    return normalizeSupportedLanguage(lang);
  };

  useEffect(() => {
    const langParam = searchParams.get("lang") || searchParams.get("language");
    const normalizedLang = normalizeLanguage(langParam);
    const normalizedParams = buildLanguageParams(normalizedLang, searchParams);

    if (i18n.language !== normalizedLang) {
      i18n.changeLanguage(normalizedLang);
    }

    if (normalizedParams.toString() !== searchParams.toString()) {
      setSearchParams(normalizedParams);
    }
  }, [i18n, searchParams, setSearchParams]);

  useEffect(() => {
    const syncScrollLock = () => {
      const shouldLock =
        navIsOpen && window.matchMedia(mobileNavigationMediaQuery).matches;

      document.body.classList.toggle(mobileNavigationClassName, shouldLock);
      document.documentElement.classList.toggle(
        mobileNavigationClassName,
        shouldLock
      );
    };

    syncScrollLock();
    window.addEventListener("resize", syncScrollLock);

    return () => {
      window.removeEventListener("resize", syncScrollLock);
      document.body.classList.remove(mobileNavigationClassName);
      document.documentElement.classList.remove(mobileNavigationClassName);
    };
  }, [navIsOpen]);

  const changeLanguage = (lang: string | null) => {
    const normalizedLang = normalizeLanguage(lang);
    const normalizedParams = buildLanguageParams(normalizedLang, searchParams);

    if (i18n.language !== normalizedLang) {
      i18n.changeLanguage(normalizedLang);
    }

    setNavIsOpen(false);

    if (normalizedParams.toString() !== searchParams.toString()) {
      setSearchParams(normalizedParams);
    }
  };

  const currentLanguage = normalizeLanguage(
    searchParams.get("lang") || searchParams.get("language") || i18n.language
  );
  const navigation = siteLinks[currentLanguage];

  const handleNavLinkClick = () => {
    setNavIsOpen(false);
  };

  return (
    <nav className="fixed-top" id="main-nav" aria-label="Primary">
      <div className="container-lg">
        <a className="site-nav__brand" href="https://esg2go.org/">
          <img alt="esg2go" className="logo" src={logoByline} />
        </a>
        <button
          className={
            "site-nav__toggle" + (navIsOpen ? " is-open" : "")
          }
          type="button"
          aria-controls="site-nav-panel"
          aria-expanded={navIsOpen}
          aria-label="Toggle navigation"
          onClick={() => setNavIsOpen(!navIsOpen)}
        >
          <span className="site-nav__toggle-bars" aria-hidden="true">
            <span className="site-nav__toggle-bar"></span>
            <span className="site-nav__toggle-bar"></span>
            <span className="site-nav__toggle-bar"></span>
          </span>
        </button>
        <div
          className={
            "site-nav__panel" + (navIsOpen ? " is-open" : "")
          }
          id="site-nav-panel"
        >
          <ul className="site-nav__list">
            {navigation.navigationItems.map((item) => (
              <li className={getNavigationItemClassName(item)} key={item.href}>
                <a
                  className="site-nav__link"
                  href={item.href}
                  onClick={handleNavLinkClick}
                >
                  {item.label}
                </a>
                {item.children?.length ? (
                  <ul className="site-nav__submenu">
                    {item.children.map((link) => (
                      <li className="site-nav__submenu-item" key={link.href}>
                        <a
                          className="site-nav__sublink"
                          href={link.href}
                          onClick={handleNavLinkClick}
                        >
                          {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
            <li className="site-nav__item site-nav__item--has-children">
              <button
                className="site-nav__button"
                type="button"
                aria-haspopup="true"
              >
                {currentLanguage.toUpperCase()}
              </button>
              <ul className="site-nav__submenu site-nav__submenu--language">
                {navigation.languageOrder.map((language) => (
                  <li className="site-nav__submenu-item" key={language}>
                    <button
                      className="site-nav__subbutton"
                      type="button"
                      onClick={() => changeLanguage(language)}
                    >
                      {language.toUpperCase()}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
